import { ActionIcon, Group, Stack, TextInput, Tooltip } from '@mantine/core';
import { useClipboard, useInputState } from '@mantine/hooks';
import { JsonEditor } from 'json-edit-react';

import CopyIcon from '@/assets/icons/content/copy.svg';
import DownloadIcon from '@/assets/icons/content/download.svg';

interface JSONEditViewerProps {
  json: Record<string, unknown>;
  filename?: string;
}

const DEFAULT_FILENAME = 'raw.json';

export const serializeJson = (json: Record<string, unknown>) => JSON.stringify(json ?? {}, null, 2);

export const buildJsonFilename = (filename?: string) => {
  const base = (filename ?? '').trim() || DEFAULT_FILENAME;
  return base.toLowerCase().endsWith('.json') ? base : `${base}.json`;
};

export const downloadJsonFile = (json: Record<string, unknown>, filename?: string) => {
  const blob = new Blob([serializeJson(json)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', buildJsonFilename(filename));

  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const JSONEditViewer = ({ json, filename }: JSONEditViewerProps) => {
  const [search, setStringValue] = useInputState('');
  const clipboard = useClipboard({ timeout: 1500 });

  const handleCopy = () => clipboard.copy(serializeJson(json));
  const handleDownload = () => downloadJsonFile(json, filename);

  return (
    <Stack gap={15}>
      <Group gap={8} wrap="nowrap" align="center">
        <TextInput
          value={search}
          onChange={setStringValue}
          placeholder="Search"
          style={{ flex: 1 }}
        />
        <Tooltip label={clipboard.copied ? 'Copied' : 'Copy JSON'}>
          <ActionIcon onClick={handleCopy} aria-label="Copy JSON" variant="subtle" size={35}>
            <CopyIcon />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Download JSON">
          <ActionIcon
            onClick={handleDownload}
            aria-label="Download JSON"
            variant="subtle"
            size={35}
          >
            <DownloadIcon />
          </ActionIcon>
        </Tooltip>
      </Group>

      <JsonEditor
        data={json}
        restrictEdit
        enableClipboard={false}
        restrictDelete
        restrictDrag
        restrictAdd
        restrictTypeSelection
        maxWidth="100%"
        collapse={(node) => !node.size}
        searchText={search}
        searchFilter="all"
        theme={{
          styles: {
            container: {
              backgroundColor: 'var(--grid-column-bg)',
              fontFamily: 'Inter',
            },
            bracket: {
              color: 'var(--json-key)',
              fontWeight: 'bold',
            },
            string: 'var(--json-string)',
            number: 'var(--json-number)',
            boolean: 'var(--json-boolean)',
            null: {
              color: 'var(--json-null)',
              fontWeight: 'bold',
            },
            itemCount: {
              color: 'var(--json-item-count)',
            },
            property: 'var(--json-key)',
          },
        }}
      />
    </Stack>
  );
};

export default JSONEditViewer;
